const COMPONENT_NAME = "z-diff-editor";
const TEMPLATE_URL = "/static/libs/components/" + COMPONENT_NAME + ".html";

export default function register() {
    class DiffEditorComponent extends HTMLElement {
        constructor() {
            super();
            this.innerHTML = null;
            this.jsLibs = [] ;
        }

        init() {
            if (this.initDone) { return; }
            this.initDone = true;

            this.doImport().then(()=>{
                let editor = document.createElement("iframe");
                editor.src = TEMPLATE_URL + "?value=" + encodeURIComponent(`${this.currentValue ? this.currentValue : ""}`) + "&language=" + this.getAttribute("language");
                editor.style = "border:0;margin:0;padding:0;width:100%;height:100%";
                this.style = "border:1px solid grey;padding:0;display:block;height:100%;width:100%;overflow:hidden";
                this.className = "rounded-top rounded-bottom";
    
                if(this.getAttribute("language") === "html") {
                    this.preview = document.createElement("iframe");
                    this.preview.style = "border-top:0px;border-bottom:0px;border-left:1px solid grey;border-right:0px;margin:0;padding:0;width:100%;height:100%;background-color:white";
                    this.appendChild(this.createTemplate());
                    this.querySelector(".col-editor").appendChild(editor);
                    this.querySelector(".col-preview").appendChild(this.preview);
                } else {
                    this.appendChild(editor);
                }
    
                if (editor.contentWindow) {
                    editor.contentWindow.onDidInitMonaco = function () {
                        this.monacoEditor = editor.contentWindow.editor;
                        this.monaco = editor.contentWindow.monaco;
                        this.monacoEditor.updateOptions({ readOnly: this.hasAttribute("readonly") });
                        if(this.preview) {
                            this.preview.contentWindow.document.documentElement.innerHTML = this.currentValue ? this.currentValue : "";
                        }
                        setTimeout(() => {
                            for(let jsLib of this.jsLibs){
                                this.addJSLib(jsLib.libSource, jsLib.name) ;
                            }
                            this.jsLibs = [] ;

                            let original = "";
                            let modified = "";
                            if(this.currentValue){
                                original = this.currentValue.original;
                                modified = this.currentValue.modified;
                            }
                            const originalModel = this.monaco.editor.createModel(
                                original,
                                this.language||"text/plain"
                            );
                            const modifiedModel = this.monaco.editor.createModel(
                                modified,
                                this.language||"text/plain"
                            );

                            this.monacoEditor.setModel({
                                original: originalModel,
                                modified: modifiedModel,
                            });
                        }, 100);
                    }.bind(this);
                }
            }) ;
        }

        loadScript(url, head){
            return new Promise((resolve, reject)=>{
                let script = document.createElement("script");
                script.async = true;
                script.type = "text/javascript";
                script.src = url;
                script.onload = function(_, isAbort) {
                    if (!script.readyState || "complete" === script.readyState) {
                        if (isAbort){
                            reject("can't load "+url) ;
                        }else{
                            resolve() ;
                        }
                    }
                };
                
                script.onreadystatechange = script.onload ;
                
                script.onerror = function () {
                    reject("can't load "+url) ;
                };
                
                head.appendChild(script);
            }) ;
        }

        doImport() {
            let rootNode = this.getRootNode();
            if(rootNode === this) { return Promise.resolve(); }
            if(!rootNode || !rootNode.getElementById){ return Promise.resolve(); }

            let head = rootNode;
            if(rootNode.head){
                head = rootNode.head ;
            }

            if(rootNode.importCodeJsPromise){ return rootNode.importCodeJsPromise; }

            rootNode.importCodeJsPromise = this.loadScript("/node_modules/prettier/standalone.js", head)
            .then(()=>{
                return this.loadScript("/node_modules/prettier/parser-html.js", head) ;
            })
            .then(()=>{
                return this.loadScript("/node_modules/prettier/parser-postcss.js", head) ;
            }) ;
            
            return rootNode.importCodeJsPromise;
        }

        createTemplate() {
            let template = document.createElement("template");
            template.innerHTML = `<div style="display:flex;height:100%">
              <div class="col-editor" style="padding:0px;flex-grow:1"></div>
              <div class="col-preview" style="padding:0px;flex-grow:1"></div>
            </div>`;
            return document.importNode(template.content, true);
        }

        connectedCallback() {
            this.init();
        }

        static get observedAttributes() {
            return ["value", "readonly", "language"];
        }

        formatCode(code, type){
            let parser = {} ;
            if(type === "html"){
                parser = {
                    parser: 'html',
                    plugins: [window.prettierPlugins.html]
                } ;
            }else if(type === "css"){
                parser = {
                    parser: 'css',
                    plugins: [window.prettierPlugins.postcss]
                } ;
            }
            try{
                return window.prettier.format(code, parser);
            }catch(err){
                console.error("Can't format code", err) ;
                return code;
            }
        }

        set value(value) {
            this.currentValue = value;
            if (this.monacoEditor) {
                let original = "";
                let modified = "";
                if(this.currentValue){
                    original = this.currentValue.original;
                    modified = this.currentValue.modified;
                }
                const originalModel = this.monaco.editor.createModel(
                    original,
                    this.language||"text/plain"
                );
                const modifiedModel = this.monaco.editor.createModel(
                    modified,
                    this.language||"text/plain"
                );
                this.monacoEditor.setModel({
                    original: originalModel,
                    modified: modifiedModel,
                });
            }
        }

        get value() {
            if (this.monacoEditor) {
                return {
                    original: this.monacoEditor.getOriginalEditor().getValue(),
                    modified: this.monacoEditor.getModifiedEditor().getValue(),
                } ;
            } else {
                return this.currentValue;
            }
        }

        set language(language) {
            this.setAttribute("language", language);
        }

        get language() {
            return this.getAttribute("language");
        }

        applyReadOnly() {
            if (this.monacoEditor) {
                this.monacoEditor.updateOptions({ readOnly: this.hasAttribute("readonly") });
            }
        }
        applyLanguage() {
            if (this.monacoEditor) {
                this.monaco.editor.setModelLanguage(this.monacoEditor.getModel(), this.language);
            }
        }

        addJSLib(libSource, name) {
            if (this.monacoEditor) {
                var libUri = 'ts:filename/'+name+'.d.ts';
                this.monaco.languages.typescript.javascriptDefaults.addExtraLib(libSource, libUri);
                this.monaco.editor.createModel(libSource, 'typescript', this.monaco.Uri.parse(libUri));
            }else{
                this.jsLibs.push({
                    libSource: libSource,
                    name: name
                }) ;
            }
        }

        attributeChangedCallback(name, oldValue, newValue) {
            switch (name) {
                case "value":
                    this.value = newValue;
                    break;
                case "readonly":
                    this.applyReadOnly();
                    break;
                case "language":
                    this.applyLanguage();
                    break;
            }
        }
    }
    customElements.define(COMPONENT_NAME, DiffEditorComponent);
}